// @flow
import React from "react";
import gql from "graphql-tag";
import { ApolloConsumer } from "@apollo/react-hooks";
import { FORM_ERROR } from "final-form";
import { Form } from "react-final-form";
import moment, { type Moment } from "moment";
import Sentry from "@nested/isomorphic-sentry";
import {
  SELLING_TIMELINE_SUBMIT,
  SELLING_TIMELINE_SUCCESS,
  SELLING_TIMELINE_FAILURE,
} from "@nested/analytics/events";
import { sendAnalyticsEvent } from "@nested/analytics";
import LandingSectionAndForm from "./LandingSection";
import EstimatedDates from "./EstimatedDates";
import Timeline from "./Timeline";
import CTA from "./CTA";
import {
  getAverageCompletionWeeks,
  isListDateInPast,
  createTimeline,
  getDaysAhead,
} from "./utils";

export const BABY_THEME = "babyTheme";

const TIME_TO_SELL = gql`
  query geoPointTimelineStats($input: GeoStatsPointFilters!) {
    geoPointStats(input: $input) {
      currentPeriod {
        averageTimeToSold
        averageTimeToExchange
      }
    }
  }
`;

type State = {
  exampleView: boolean,
  listDateInPast: boolean,
  postcode: ?string,
  moveDate: Moment,
  averageTimeToExchange: ?number,
  averageTimeToSold: ?number,
};

type Props = {
  today?: Moment,
  route: {
    props?: {
      theme?: string,
    },
  },
};

type SubmitProps = {
  address: {
    postcode: string,
    postcodeLat: number,
    postcodeLon: number,
    id: String,
  },
  moveDate: Moment,
};

class WhenShouldISell extends React.Component<Props, State> {
  static defaultProps = {
    today: moment(),
  };

  state = {
    exampleView: true,
    listDateInPast: false,
    moveDate: null,
    postcode: null,
    averageTimeToExchange: null,
    averageTimeToSold: null,
  };

  createSubmitHandler = (client: any) => async ({
    address: { postcode, postcodeLat, postcodeLon, id },
    moveDate,
  }: SubmitProps) => {
    try {
      const { today } = this.props;
      sendAnalyticsEvent({
        event: SELLING_TIMELINE_SUBMIT,
        addressId: id,
        desiredMoveDate: moveDate.format("YYYY-MM-DD"),
        desiredMoveDateDaysAhead: getDaysAhead(moveDate, today),
      });

      if (!postcodeLat || !postcodeLon || !moveDate) {
        throw new Error("Missing required fields");
      }

      const response = await client.query({
        query: TIME_TO_SELL,
        variables: {
          input: {
            longitude: postcodeLon,
            latitude: postcodeLat,
          },
        },
      });

      const stats = response?.data?.geoPointStats;

      if (!stats) {
        throw Error("Timeline request failed");
      }

      const {
        currentPeriod: { averageTimeToExchange, averageTimeToSold },
      } = stats;

      if (averageTimeToExchange && averageTimeToSold) {
        const { listDate, listDateInPast } = isListDateInPast(
          moveDate,
          averageTimeToExchange,
          today,
        );

        sendAnalyticsEvent({
          event: SELLING_TIMELINE_SUCCESS,
          addressId: id,
          timelineSuggestedListDate: listDate,
          timelineListDateInPast: listDateInPast,
        });

        this.setState({
          exampleView: false,
          listDateInPast,
          averageTimeToExchange,
          averageTimeToSold,
          moveDate,
          postcode,
        });
      } else {
        return {
          [FORM_ERROR]: "Sorry we don't cover your area (yet).",
        };
      }

      return undefined;
    } catch (e) {
      Sentry.captureException(e);
      sendAnalyticsEvent({
        event: SELLING_TIMELINE_FAILURE,
        addressId: id,
        postcode,
        error: e.message,
      });

      return {
        [FORM_ERROR]: "Failed to get an estimate, please try again later",
      };
    }
  };

  render() {
    const {
      exampleView,
      moveDate,
      averageTimeToExchange,
      averageTimeToSold,
      postcode,
      listDateInPast,
    } = this.state;

    const { route, today } = this.props;
    const babyTheme = route.props?.theme === BABY_THEME;

    const timelineData = createTimeline(
      exampleView,
      listDateInPast,
      moveDate,
      averageTimeToExchange,
      averageTimeToSold,
      today,
    );

    const averageCompletionWeeks = getAverageCompletionWeeks(
      averageTimeToExchange,
    );

    return (
      <ApolloConsumer>
        {(client) => (
          <Form
            initialValues={{
              address: null,
              moveDate: null,
            }}
            onSubmit={this.createSubmitHandler(client)}
          >
            {({ handleSubmit, submitError }) => (
              <form onSubmit={handleSubmit} className="CreateProspectForm">
                <LandingSectionAndForm
                  babyTheme={babyTheme}
                  submitError={submitError}
                  exampleView={exampleView}
                  today={today}
                />
                <EstimatedDates
                  exampleView={exampleView}
                  desiredMoveDate={moveDate}
                  createdCompletionDate={timelineData.dates.completion}
                  listDate={timelineData.dates.listed}
                  postcode={postcode}
                  averageCompletionWeeks={averageCompletionWeeks}
                  listDateInPast={listDateInPast}
                  babyTheme={babyTheme}
                />
                <Timeline
                  exampleView={exampleView}
                  timelineData={timelineData}
                  listDateInPast={listDateInPast}
                />
                <CTA />
              </form>
            )}
          </Form>
        )}
      </ApolloConsumer>
    );
  }
}

export default WhenShouldISell;
